import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  AccountPortfolio,
  Position,
  PositionCustomStrategyData,
  PositionCloseData,
  PositionMetadataData,
  PositionStrategyTagsData,
  PositionsResponse,
  StrategyDescriptionTag,
  StrategyTagDeleteData,
} from './types/trading.ts';

export interface StrategyTagCreateParams {
  name: string;
  description?: string;
  color?: string;
}

export interface StrategyTagUpdateParams {
  name?: string;
  description?: string | null;
  color?: string | null;
}

type StrategyTagListResponse = APIResponse & { data?: { tags: StrategyDescriptionTag[] } };
type StrategyTagResponse = APIResponse & { data?: { tag: StrategyDescriptionTag } };

/** Account snapshots, positions, and position-strategy metadata endpoints. */
export class AccountsApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getPortfolio(): Promise<AccountPortfolio> {
    return this.request<AccountPortfolio>(`${this.baseUrl}/account/portfolio`, {
      timeoutMs: 15000,
      retries: 1,
    });
  }

  getPositions(): Promise<PositionsResponse> {
    return this.request<PositionsResponse>(`${this.baseUrl}/account/positions`, {
      timeoutMs: 15000,
      retries: 1,
    });
  }

  getStrategyDescriptionTags(): Promise<StrategyTagListResponse> {
    return this.request<StrategyTagListResponse>(`${this.baseUrl}/account/strategy-tags`, {
      timeoutMs: 8000,
      retries: 1,
    });
  }

  createStrategyDescriptionTag(payload: StrategyTagCreateParams): Promise<StrategyTagResponse> {
    return this.request<StrategyTagResponse>(`${this.baseUrl}/account/strategy-tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  updateStrategyDescriptionTag(tagId: number, payload: StrategyTagUpdateParams): Promise<StrategyTagResponse> {
    return this.request<StrategyTagResponse>(`${this.baseUrl}/account/strategy-tags/${tagId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  deleteStrategyDescriptionTag(tagId: number): Promise<APIResponse<StrategyTagDeleteData>> {
    return this.request<APIResponse<StrategyTagDeleteData>>(`${this.baseUrl}/account/strategy-tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  updatePositionStrategyTags(
    positionKey: string,
    tagIds: number[],
  ): Promise<APIResponse<PositionStrategyTagsData>> {
    return this.request<APIResponse<PositionStrategyTagsData>>(`${this.baseUrl}/account/position-tags`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position_key: positionKey, tag_ids: tagIds }),
    });
  }

  updatePositionCustomStrategy(
    positionKey: string,
    strategyName: string | null,
  ): Promise<APIResponse<PositionCustomStrategyData>> {
    return this.request<APIResponse<PositionCustomStrategyData>>(`${this.baseUrl}/account/position-strategy-name`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position_key: positionKey, strategy_name: strategyName }),
    });
  }

  updatePositionMetadata(
    positionKey: string,
    strategyName: string | null,
    tagIds: number[],
  ): Promise<APIResponse<PositionMetadataData>> {
    return this.request<APIResponse<PositionMetadataData>>(`${this.baseUrl}/account/position-metadata`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        position_key: positionKey,
        strategy_name: strategyName,
        tag_ids: tagIds,
      }),
    });
  }

  closePosition(
    symbol: string,
    secType?: string,
    contract?: Partial<Position>,
  ): Promise<APIResponse<PositionCloseData>> {
    let endpoint = `${this.baseUrl}/account/positions/${encodeURIComponent(symbol)}/close`;
    const params = new URLSearchParams();
    if (secType) params.set('sec_type', secType);
    if (contract?.con_id !== undefined && contract.con_id !== null) {
      params.set('con_id', String(contract.con_id));
    }
    if (contract?.exchange) params.set('exchange', contract.exchange);
    if (contract?.currency) params.set('currency', contract.currency);
    if (contract?.local_symbol) params.set('local_symbol', contract.local_symbol);
    if (contract?.trading_class) params.set('trading_class', contract.trading_class);
    if (contract?.last_trade_date_or_contract_month) {
      params.set('last_trade_date_or_contract_month', contract.last_trade_date_or_contract_month);
    }
    if (contract?.multiplier) params.set('multiplier', contract.multiplier);
    const query = params.toString();
    if (query) endpoint += `?${query}`;
    return this.request<APIResponse<PositionCloseData>>(endpoint, { method: 'POST' });
  }
}
